import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { Building } from "lucide-react";

// Espace freelance pro (revue #359) : identification légale de Constrigo
// portée sur les autofactures des freelances (mentions obligatoires d'une
// facture). Tant qu'un champ est vide, le relevé affiche « à compléter ».
// Endpoints : admin.commissions.billingSettings / saveBillingSettings.

const FIELDS: Array<{ key: "legalName" | "legalForm" | "siren" | "vatNumber" | "addressLine1" | "addressLine2" | "postalCode" | "city" | "email"; label: string; required?: boolean }> = [
  { key: "legalName", label: "Raison sociale", required: true },
  { key: "legalForm", label: "Forme juridique (ex. SAS)" },
  { key: "siren", label: "SIREN", required: true },
  { key: "vatNumber", label: "N° TVA intracommunautaire", required: true },
  { key: "addressLine1", label: "Adresse", required: true },
  { key: "addressLine2", label: "Complément d'adresse" },
  { key: "postalCode", label: "Code postal", required: true },
  { key: "city", label: "Ville", required: true },
  { key: "email", label: "E-mail de contact" },
];

export function BillingSettingsPanel() {
  const utils = trpc.useUtils();
  const q = trpc.admin.commissions.billingSettings.useQuery();
  const [form, setForm] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!q.data) return;
    const next: Record<string, string> = {};
    for (const f of FIELDS) next[f.key] = q.data[f.key] ?? "";
    setForm(next);
  }, [q.data]);
  const save = trpc.admin.commissions.saveBillingSettings.useMutation({
    onSuccess: () => { toast.success("Identification légale enregistrée."); void utils.admin.commissions.billingSettings.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Building className="h-4 w-4" /> Identification légale sur les autofactures
          {q.data && (q.data.complete ? <Badge variant="default">Complète</Badge> : <Badge variant="warning">À compléter</Badge>)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">Mentions obligatoires du client (Constrigo) sur chaque relevé valant facture établi au nom des freelances.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">{f.label}{f.required ? " *" : ""}</Label>
              <Input value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            </div>
          ))}
        </div>
        <Button size="sm" disabled={save.isLoading} onClick={() => save.mutate(form as any)}>Enregistrer</Button>
      </CardContent>
    </Card>
  );
}
